import React, { useState, useEffect } from 'react';
import { InputLabel, FormLabel, FormControl, FormControlLabel, Box, TextField, Checkbox, IconButton, Typography, InputAdornment, Tooltip, ToggleButtonGroup, ToggleButton } from "@mui/material"
import CircleCheckedFilled from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import CircleUnchecked from '@mui/icons-material/RadioButtonUnchecked';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { FormikProps } from "formik";
import { useDrop } from 'react-dnd'
import { useSelector } from 'react-redux';
import { useAppDispatch } from "../../store/hooks";
import { IAudio } from "../../types/interfaces/audio.interface";
import { setSelectedAudios } from '../../store/resuders/audio.reducer';
import { getSelectedAudios } from "../../store/selectors/audio.selector";
import { useTranslation } from 'react-i18next';

export interface IProps {
    formik: FormikProps<any>;
    segmentName?: string;
    type?: string;
    language?: string;
}

export const ListenBeta = ({ formik }: IProps) => {
    // Access the formik values and helpers as needed
    const { values, handleChange } = formik;
    
    const selectedAudios = useSelector(getSelectedAudios);
    const dispatch = useAppDispatch();
    const [useAudioSegmentation, setUseAudioSegmentation] = useState(false);
    const { t } = useTranslation();
    const [audios, setAudios] = useState<IAudio[]>(Array.isArray(selectedAudios) ? selectedAudios.slice(0, 1) : []);
    const comparisonMode = values?.comparison_mode || 'transcription';

    const addAudioFromLibrary = (item: any) => {
        if (!(item && item.itemType === 'audios')) {
            return;
        }

        if (audios[0]?.libraryID === item.libraryID) {
            return;
        }

        const updatedAudios = [item];
        setAudios(updatedAudios);
        dispatch(setSelectedAudios(updatedAudios));
    }

    const [{ isOver: isOverAudio }, audioDrop] = useDrop(() => ({
        accept: 'ROWS',
        drop: addAudioFromLibrary,
        collect: (monitor: any) => ({
            isOver: !!monitor.isOver(),
        }),
    }), [audios])

    const handleRemoveAudio = () => {
        setAudios([]);
        dispatch(setSelectedAudios([]));
    };

    const handleAudioSegmentation = () => {
        setUseAudioSegmentation(!useAudioSegmentation);
        handleChange({ target: { name: 'segment_audio', value: !useAudioSegmentation } });
    }

    const handleComparisonModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: 'transcription' | 'waveform' | null) => {
        if (!newMode) return;
        formik.setFieldValue('comparison_mode', newMode);
    };
    
     useEffect(() => {
            if (comparisonMode === 'waveform') {
                formik.setFieldValue('libraryItemIds', audios?.map(a => ({ libraryID: a.libraryID })));
            }
        }, [audios]);

    useEffect(() => {
        if (!Array.isArray(selectedAudios)) {
            setAudios([]);
            return;
        }

        const trimmed = selectedAudios.slice(0, 1);
        setAudios(trimmed);

        if (selectedAudios.length > 1) {
            dispatch(setSelectedAudios(trimmed));
        }
    }, [dispatch, selectedAudios]);

    useEffect(() => {
        setUseAudioSegmentation(values?.segment_audio || false);
    }, [values?.segment_audio]);

    // Reusable tooltip component for parameters
    const ParameterTooltip = ({ title }: { title: string }) => (
        <Tooltip title={title} arrow placement="top">
            <InfoOutlined sx={{ fontSize: 16, ml: 0.5, color: 'text.secondary', cursor: 'default' }} />
        </Tooltip>
    );
    return (
        <>
            <Box className='prompt-select' sx={{ mb: 2 }}>
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <FormLabel component="legend" sx={{ mb: 1 }}>Comparison Mode</FormLabel>
                    <ToggleButtonGroup
                        exclusive
                        value={comparisonMode}
                        onChange={handleComparisonModeChange}
                        color="primary"
                        size="small"
                        fullWidth
                    >
                        <ToggleButton value="transcription">Transcription</ToggleButton>
                        <ToggleButton value="waveform">Waveform</ToggleButton>
                    </ToggleButtonGroup>
                </FormControl>
            </Box>
            {comparisonMode === 'waveform' && (
                <Box
                    className='prompt-select'
                    ref={audioDrop}
                    sx={{
                        borderRadius: '8px',
                        transition: 'background-color 0.2s ease, border-color 0.2s ease',
                        backgroundColor: !audios.length && isOverAudio ? '#f4fff4' : 'transparent',
                    }}
                >
                    <FormControl sx={{ width: "100%" }}>
                        <FormLabel sx={{ mb: 1, fontWeight: 600 }}>{t("Audio File")}</FormLabel>
                        {!audios.length ? (
                            <Box
                                sx={{
                                    border: '1px dashed',
                                    borderColor: isOverAudio ? '#58A148' : '#cfd8dc',
                                    borderRadius: '8px',
                                    padding: '18px',
                                    textAlign: 'center',
                                    color: isOverAudio ? '#2e7d32' : 'text.secondary',
                                    backgroundColor: '#fafafa',
                                    fontSize: '0.95rem',
                                }}
                            >
                                {t('Drag an audio from the library to use as the reference waveform.')}
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    border: '1px solid #d0d7de',
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    backgroundColor: '#fdfdfd',
                                }}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {audios[0].name}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {t('Library ID')}: {audios[0].libraryID}
                                    </Typography>
                                </Box>
                                <IconButton
                                    onClick={handleRemoveAudio}
                                    size="small"
                                    aria-label="Remove audio"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        )}
                    </FormControl>
                </Box>
            )}

            {comparisonMode === 'transcription' && (
                <Box className='prompt-select'>
                    <FormControl sx={{ width: "100%" }}>
                        <InputLabel id="case-editor-prompt"></InputLabel>
                        <TextField
                            id="prompt"
                            name='prompt'
                            value={values.prompt}
                            label={t("Prompt")}
                            onChange={handleChange}
                            multiline
                            rows={3}
                            sx={{
                                '& .MuiInputBase-root': {
                                    backgroundColor: '#f5f5f5'
                                }
                            }}
                        />
                    </FormControl>
                    {/* Audio Segmentation - Hidden */}
                    {/* <FormControl>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FormControlLabel
                                name="case-audio-segmentation"
                                control={
                                    <Checkbox
                                        id='segment_audio'
                                        name='segment_audio'
                                        icon={<CircleUnchecked />}
                                        checkedIcon={<CircleCheckedFilled />}
                                        checked={values?.segment_audio}
                                        onChange={handleAudioSegmentation}
                                    />
                                }
                                label="Audio Segmentation"
                                labelPlacement="end"
                            />
                            <ParameterTooltip title="Audio segmentation splits recordings along natural speech boundaries to improve transcription accuracy across different languages and speakers." />
                        </Box>
                    </FormControl> */}
                </Box>
            )}

            {/* Audio Segmentation Parameters - Hidden */}
            {/* {comparisonMode === 'transcription' && useAudioSegmentation && (
                <Box sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '16px',
                    marginTop: '16px',
                    marginBottom: '16px',
                    backgroundColor: '#fafafa'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            Audio Segmentation Parameters
                        </Typography>
                        <ParameterTooltip title="These parameters should generally be left at their default values." />
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                        <Box sx={{ display: 'flex', width: '100%', gap: '12px' }}>
                            <FormControl sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <TextField
                                        id="min_silence_len"
                                        name="min_silence_len"
                                        type="number"
                                        label="Min Silence Length (ms)"
                                        value={values?.min_silence_len}
                                        onChange={handleChange}
                                        inputProps={{ min: 50, max: 2000, step: 50 }}
                                        size="small"
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <ParameterTooltip title="Minimum duration of silence (in ms) required to consider it a segment boundary. Lower values detect shorter pauses but may create more segments." />
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Box>
                            </FormControl>
                            <FormControl sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <TextField
                                        id="silence_thresh"
                                        name="silence_thresh"
                                        type="number"
                                        label="Silence Threshold (dBFS)"
                                        value={values?.silence_thresh}
                                        onChange={handleChange}
                                        inputProps={{ min: -100, max: -20, step: 5 }}
                                        size="small"
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <ParameterTooltip title="Audio level threshold (in dBFS) to classify as silence. More negative values = quieter threshold. -70 works well for most recordings." />
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Box>
                            </FormControl>
                        </Box>
                        <Box sx={{ display: 'flex', width: '100%', gap: '12px' }}>
                            <FormControl sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <TextField
                                        id="keep_silence"
                                        name="keep_silence"
                                        type="number"
                                        label="Segment Buffer (ms)"
                                        value={values?.keep_silence}
                                        onChange={handleChange}
                                        inputProps={{ min: 0, max: 2000, step: 50 }}
                                        size="small"
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <ParameterTooltip title="Buffer time (in ms) added around each segment to prevent cutting off trailing speech. Helps ensure words aren't clipped at segment boundaries." />
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Box>
                            </FormControl>
                            <FormControl sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <TextField
                                        id="min_segment_duration"
                                        name="min_segment_duration"
                                        type="number"
                                        label="Min Segment Duration (ms)"
                                        value={values?.min_segment_duration}
                                        onChange={handleChange}
                                        inputProps={{ min: 100, max: 10000, step: 50 }}
                                        size="small"
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <ParameterTooltip title="Minimum length (in ms) for a valid audio segment. Segments shorter than this will be merged with adjacent segments." />
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Box>
                            </FormControl>
                        </Box>
                    </Box>
                </Box>
            )} */}

            <Box className='prompt-select'>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* Final Silence */}
                    <FormControl sx={{ flex: 1 }}>
                        <TextField
                            id="finalSilence"
                            name="finalSilence"
                            type="number"
                            label={t('Final Silence')}
                            value={values?.finalSilence}
                            onChange={handleChange}
                            inputProps={{ min: 0, max: 10, step: 0.05 }}
                            size="small"
                            fullWidth
                        />
                    </FormControl>

                    {/* Prompt Timeout */}
                    <FormControl sx={{ flex: 1 }}>
                        <TextField
                            id="stepTimeout"
                            name="stepTimeout"
                            type="number"
                            label={t('Prompt Timeout')}
                            value={values?.stepTimeout}
                            onChange={handleChange}
                            inputProps={{ min: 0, max: 300, step: 1 }}
                            size="small"
                            fullWidth
                        />
                    </FormControl>

                    {/* Prompt Confidence */}
                    <FormControl sx={{ flex: 1 }}>
                        <TextField
                            id="promptConfidenceLevel"
                            name="promptConfidenceLevel"
                            type="number"
                            label={t('Prompt Confidence')}
                            value={values?.promptConfidenceLevel}
                            onChange={handleChange}
                            inputProps={{ min: 0, max: 1, step: 0.01 }}
                            size="small"
                            fullWidth
                        />
                    </FormControl>
                </Box>
            </Box>
        </>
    );
};
